import { createSelector, createSlice, isAnyOf } from '@reduxjs/toolkit';
import { getCountries, getCountryCallingCode, parsePhoneNumberFromString } from 'libphonenumber-js';
import {
  displayWarningForMissingAddressNumber,
  zipCodeHasErrors,
  isRestrictedPostalCode,
} from '/Foundation/React/code/Utilities/AddressValidation';
import {
  lookupShippingAddress,
  getShippingMethods,
  getExternalShippingMethods,
  setShippingMethod,
  showBillingFields,
  setShippingAddress,
  setAddresses,
  setContact,
} from '../Thunks/ShippingThunks';
import { getCart } from '../Thunks/CartThunks.jsx';
import { createNewField, formatPhoneNumber, setAddressFromApi } from '../Helpers/StateHelper.js';
import { showStateField } from '../Helpers/UiHelper.js';
import { handlePfasProducts } from '../Helpers/GeneralHelper.js';
import { SHIPPING_CONSTANTS } from '../Utils/Constants.js';
import { objectShallowEqual, shouldPreCheckInput } from '/Foundation/Common/code/Utils/Utils';
import { br_isEnabled } from '/Foundation/Bloomreach/code/Events/Bloomreach.js';
import {
  addressHasErrors,
  textHasErrors,
  fieldIsEmpty,
  emailHasErrors,
} from '/Foundation/Common/code/Helpers/ValidationHelper.js';

const reintroduceContactPage = Boolean(window._THULEDATA?.ReintroduceContactPage);

const initialState = {
  reintroduceContactPage,
  email: createNewField(),
  billing: {
    firstName: createNewField(),
    lastName: createNewField(),
    address: createNewField(),
    address2: createNewField(),
    zipCode: createNewField(),
    city: createNewField(),
    state: createNewField(),
    country: createNewField(),
  },
  billingFromCart: null,
  shipping: {
    firstName: createNewField(),
    lastName: createNewField(),
    address: createNewField(),
    address2: createNewField(),
    zipCode: createNewField(),
    city: createNewField(),
    state: createNewField(),
    country: createNewField(),
    phone: createNewField(),
    phoneCountryCode: createNewField(_THULEDATA?._Checkout?.market),
  },
  shippingSteps: {
    isContact: reintroduceContactPage,
    isShippingAddress: !reintroduceContactPage,
    isBillingAddress: false,
    isShippingMethods: false,
    isPayment: false,
  },
  isRestrictedPostalCode: false,
  isMissingHouseNumber: false,
  addressIsEmpty: false,
  shippingRestrictions: {
    isRestrictedState: false,
    restrictedStates: [],
  },
  useSameAddressForBilling: true,
  hasDoneAddressLookup: false,
  addressSuggestions: null,
  selectedSuggestion: null,
  suggestionValidated: false,
  shippingMethods: [],
  selectedShippingMethod: '',
  selectedExternalShippingMethod: null,
  nshiftOptionsData: null,
  nshiftEnabled: Boolean(window?.nShiftCheckoutWidget),
  loading: false,
  messages: [],
  shippingMethodCache: {},
  enableHouseNumberWarning: window._THULEDATA?._Checkout?.enableHouseNumberWarning || false,
  newsletterEnabled: br_isEnabled(),
  wantNewsletter: shouldPreCheckInput(),
  alreadySignedUpForNewsletter: false,
  phoneCountryCodes: getCountries()
    .filter((code) => _THULEDATA?._Checkout?.availableMarkets.includes(code))
    .map((code) => ({
      code,
      dialCode: getCountryCallingCode(code),
    }))
    .sort((a, b) => a.code.localeCompare(b.code)),
  showCityFirstInAddress: window._THULEDATA?._Checkout?.showCityFirstInAddress || false,
  hasAddressFromProfile: false,
};

export const ShippingSlice = createSlice({
  name: 'shipping',
  initialState,
  reducers: {
    setEmail: (state, { payload }) => {
      state.email.value = payload;
      state.email.touched = true;
    },
    validateEmail: (state, { payload }) => {
      state.email.hasError = emailHasErrors(payload);
    },
    resetEmailError: (state) => {
      state.email.hasError = false;
    },
    setUseSameAddress: (state, { payload }) => {
      state.useSameAddressForBilling = payload;

      if (!payload) {
        state.billing.firstName.value = '';
        state.billing.lastName.value = '';
        state.billing.address.value = '';
        state.billing.address2.value = '';
        state.billing.zipCode.value = '';
        state.billing.city.value = '';
        state.billing.state.value = '';
        state.billing.country.value = state.shipping.country.value;
      }
    },
    updateAddressField: (state, { payload }) => {
      state[payload.addressType][payload.field].value = payload.value;
      state[payload.addressType][payload.field].touched = true;

      if (payload.addressType === 'shipping') {
        state.hasDoneAddressLookup = false;
        state.addressSuggestions = null;
        state.selectedSuggestion = null;
        state.suggestionValidated = false;
      }
    },
    validateAddressField: (state, { payload }) => {
      let hasValidationError = true;

      switch (payload.field) {
        case 'firstName':
          hasValidationError = textHasErrors(state[payload.addressType][payload.field].value);
          break;
        case 'lastName':
          hasValidationError = textHasErrors(state[payload.addressType][payload.field].value);
          break;
        case 'address':
          hasValidationError = fieldIsEmpty(state[payload.addressType][payload.field].value);
          state.addressIsEmpty = hasValidationError;

          if (!hasValidationError) {
            hasValidationError =
              payload.addressType === 'shipping'
                ? addressHasErrors(state[payload.addressType][payload.field].value)
                : false;
          }

          if (!hasValidationError && payload.addressType === 'shipping') {
            state.isMissingHouseNumber = displayWarningForMissingAddressNumber(
              state[payload.addressType][payload.field].value,
              state.enableHouseNumberWarning
            );
          }
          break;
        case 'address2':
          hasValidationError =
            payload.addressType === 'shipping'
              ? addressHasErrors(state[payload.addressType][payload.field].value)
              : false;
          break;
        case 'zipCode':
          state.isRestrictedPostalCode = false;

          hasValidationError = zipCodeHasErrors(
            state[payload.addressType][payload.field].value,
            window._THULEDATA._Checkout.market
          );
          if (payload.addressType === 'shipping') {
            state.isRestrictedPostalCode = isRestrictedPostalCode(
              state[payload.addressType][payload.field].value,
              window._THULEDATA._Checkout.market
            );
          }
          break;
        case 'city':
          hasValidationError = textHasErrors(state[payload.addressType][payload.field].value);
          break;
        case 'state':
          hasValidationError = fieldIsEmpty(state[payload.addressType][payload.field].value);

          if (!hasValidationError && payload.addressType === 'shipping') {
            state.shippingRestrictions.isRestrictedState = state.shippingRestrictions.restrictedStates.includes(
              state[payload.addressType][payload.field].value
            );
          }

          break;
        case 'country':
          hasValidationError = false;
        // falls through
        case 'phone':
          if (
            !state[payload.addressType][payload.field].value ||
            state[payload.addressType][payload.field].value.startsWith('+') ||
            !parseInt(state[payload.addressType][payload.field].value)
          )
            break;

          hasValidationError = !parsePhoneNumberFromString(
            state[payload.addressType][payload.field].value,
            state[payload.addressType].phoneCountryCode.value
          )?.isValid();
          break;
        case 'phoneCountryCode':
          state[payload.addressType][payload.field].hasError = false;

          if (
            !state[payload.addressType].phone.value ||
            state[payload.addressType].phone.value.startsWith('+') ||
            !parseInt(state[payload.addressType].phone.value)
          )
            break;

          hasValidationError = !parsePhoneNumberFromString(
            state[payload.addressType].phone.value,
            state[payload.addressType][payload.field].value
          )?.isValid();

          payload.field = 'phone';
          break;
      }

      state[payload.addressType][payload.field].hasError = hasValidationError;
    },
    resetValidationError: (state, { payload }) => {
      state[payload.addressType][payload.field].hasError = false;
    },
    validateShippingAddress: (state) => {
      let fields = Object.keys(state.shipping);

      if (!showStateField(window._THULEDATA._Checkout.states)) fields = fields.filter((x) => x !== 'state');

      fields.forEach((field) => {
        ShippingSlice.caseReducers.validateAddressField(state, {
          payload: { addressType: 'shipping', field: field.toString() },
        });
      });
    },
    validateBillingAddress: (state) => {
      let fields = Object.keys(state.billing);

      if (!showStateField(window._THULEDATA._Checkout.states)) fields = fields.filter((x) => x !== 'state');

      fields.forEach((field) => {
        ShippingSlice.caseReducers.validateAddressField(state, {
          payload: { addressType: 'billing', field: field.toString() },
        });
      });
    },
    setShippingAddressFromCart: (state, { payload }) => {
      let address = payload.shippingAddress;
      address.countryName = payload.countryName;
      setAddressFromApi(state, 'shipping', address);
    },
    setBillingAddressFromCart: (state, { payload }) => {
      let address = payload.billingAddress;
      address.countryName = payload.countryName;

      const normalizedShippingAddress = {
        firstName: state.shipping.firstName.value || '',
        lastName: state.shipping.lastName.value || '',
        streetName: state.shipping.address.value || '',
        additionalStreetInfo: state.shipping.address2.value || '',
        postalCode: state.shipping.zipCode.value || '',
        city: state.shipping.city.value || '',
        state: state.shipping.state.value || '',
        countryName: state.shipping.country.value || '',
      };

      const normalizedBillingAddress = {
        firstName: address.firstName || '',
        lastName: address.lastName || '',
        streetName: address.streetName || '',
        additionalStreetInfo: address.additionalStreetInfo || '',
        postalCode: address.postalCode || '',
        city: address.city || '',
        state: address.state || '',
        countryName: address.countryName || '',
      };

      state.useSameAddressForBilling = objectShallowEqual(normalizedShippingAddress, normalizedBillingAddress);
      setAddressFromApi(state, 'billing', address);
    },
    setFormattedPhoneNumber: (state, { payload }) => {
      formatPhoneNumber(state, 'shipping', payload);
    },
    setShippingAddressFromUser: (state, { payload }) => {
      setAddressFromApi(state, 'shipping', payload);
      state.hasAddressFromProfile = true;
    },
    setBillingAddressToShippingAddress: (state) => {
      state.billing.firstName = state.shipping.firstName;
      state.billing.lastName = state.shipping.lastName;
      state.billing.address = state.shipping.address;
      state.billing.address2 = state.shipping.address2;
      state.billing.zipCode = state.shipping.zipCode;
      state.billing.city = state.shipping.city;
      state.billing.state = state.shipping.state;
      state.billing.country = state.shipping.country;
    },
    setCountry: (state, { payload }) => {
      state.shipping.country.value = state.billing.country.value = payload;
    },
    goToContact: (state) => {
      state.shippingSteps.isContact = true;
      state.shippingSteps.isShippingAddress = false;
      state.shippingSteps.isBillingAddress = false;
      state.shippingSteps.isShippingMethods = false;
      state.shippingSteps.isPayment = false;
    },
    goToShippingAddress: (state) => {
      state.shippingSteps.isContact = false;
      state.shippingSteps.isShippingAddress = true;
      state.shippingSteps.isBillingAddress = false;
      state.shippingSteps.isShippingMethods = false;
      state.shippingSteps.isPayment = false;
    },
    goToBillingAddress: (state) => {
      state.shippingSteps.isContact = false;
      state.shippingSteps.isShippingAddress = false;
      state.shippingSteps.isBillingAddress = true;
      state.shippingSteps.isShippingMethods = false;
      state.shippingSteps.isPayment = false;
      location.href = '#checkout-wrapper';
    },
    goToShippingMethods: (state) => {
      state.shippingSteps.isContact = false;
      state.shippingSteps.isShippingAddress = false;
      state.shippingSteps.isBillingAddress = false;
      state.shippingSteps.isShippingMethods = true;
      state.shippingSteps.isPayment = false;
    },
    goToPayment: (state) => {
      state.shippingSteps.isContact = false;
      state.shippingSteps.isShippingAddress = false;
      state.shippingSteps.isBillingAddress = false;
      state.shippingSteps.isShippingMethods = false;
      state.shippingSteps.isPayment = true;
    },
    setSelectedSuggestion: (state, { payload }) => {
      state.selectedSuggestion = payload;
      state.shipping.address.value = state.addressSuggestions[payload].address;
      state.shipping.address2.value = state.addressSuggestions[payload].address2;
      state.shipping.zipCode.value = state.addressSuggestions[payload].zipCode;
      state.shipping.city.value = state.addressSuggestions[payload].city;
      state.shipping.state.value = state.addressSuggestions[payload].state;

      state.shippingRestrictions.isRestrictedState = state.shippingRestrictions.restrictedStates.includes(
        state.shipping.state.value
      );
    },
    setIsValidated: (state) => {
      state.suggestionValidated = true;
    },
    setSelectedShippingMethod: (state, { payload }) => {
      state.selectedShippingMethod = payload;
    },
    setSelectedExternalShippingMethod: (state, { payload }) => {
      state.selectedExternalShippingMethod = payload;
    },
    addMessage: (state, { payload }) => {
      state.messages.push(payload);
    },
    removeMessage: (state, { payload }) => {
      state.messages = state.messages.filter((x) => x.id != payload);
    },
    clearMessages: (state) => {
      state.messages = [];
    },
    setWantNewsletter: (state) => {
      state.wantNewsletter = !state.wantNewsletter;
    },
    setAlreadySignedUpForNewsletter: (state, { payload }) => {
      state.alreadySignedUpForNewsletter = payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getCart.fulfilled, (state, { payload }) => {
      if (
        payload.Shipping?.length > 0 &&
        payload.Shipping[0].Name?.includes(SHIPPING_CONSTANTS.SHIPPING_ADDRESS_NAME_PREFIX)
      ) {
        state.hasDoneAddressLookup = true;
      }

      state.shippingRestrictions.restrictedStates = [...(payload.restrictedStates || [])];

      //Check for shipping restrictions for preloaded address
      const stateSetOnCart = state.shipping.state.value;
      if (stateSetOnCart) {
        state.shippingRestrictions.isRestrictedState =
          state.shippingRestrictions.restrictedStates.includes(stateSetOnCart);
      }

      if (payload.ShippingMethods?.length > 0) {
        let shippingMethods = payload.ShippingMethods;
        state.shippingMethods = [...shippingMethods];

        if (payload.ShippingInfo?.length > 0) state.selectedShippingMethod = payload.ShippingInfo[0]?.ShippingMethodID;
      }
    });
    builder.addCase(lookupShippingAddress.fulfilled, (state, { payload }) => {
      state.loading = false;
      var addressList = payload.validatedAddressList ?? payload.ValidatedAddressList;
      if (addressList) {
        const addressSuggestions = addressList.map((suggestion) => {
          return {
            address: suggestion.line1,
            address2: suggestion.line2 && suggestion.line2 != 'NA' ? suggestion.line2 : '',
            city: suggestion.city,
            zipCode: suggestion.postalCode,
            state: suggestion.region,
          };
        });

        const oldAddress = {
          address: state.shipping.address.value,
          address2: state.shipping.address2.value,
          zipCode: state.shipping.zipCode.value,
          city: state.shipping.city.value,
          state: state.shipping.state.value,
        };

        state.hasDoneAddressLookup = true;

        const validatedAddressEqualsEnteredAddress =
          addressSuggestions?.length == 1 && objectShallowEqual(addressSuggestions[0], oldAddress);

        if (!validatedAddressEqualsEnteredAddress) {
          state.addressSuggestions = [...addressSuggestions, { ...oldAddress }];
          ShippingSlice.caseReducers.setSelectedSuggestion(state, { payload: 0 });
        }
      }
    });
    builder.addCase(setShippingAddress.fulfilled, (state, { payload }) => {
      state.loading = false;
      let shippingAddress = payload.shippingAddress;
      state.email.value = shippingAddress.email;
      state.shipping.firstName.value = shippingAddress.firstName;
      state.shipping.lastName.value = shippingAddress.lastName;
      state.shipping.address.value = shippingAddress.streetName;
      state.shipping.address2.value = shippingAddress.additionalStreetInfo;
      state.shipping.zipCode.value = shippingAddress.postalCode;
      state.shipping.city.value = shippingAddress.city;
      state.shipping.state.value = shippingAddress.state;
      state.shipping.phone.value = shippingAddress.phone;
      state.shipping.country.value = payload.countryName;
    });
    builder.addCase(setAddresses.fulfilled, (state, { payload }) => {
      let shippingAddress = payload.shippingAddress;
      if (shippingAddress) {
        state.email.value = shippingAddress.email;
        state.shipping.firstName.value = shippingAddress.firstName;
        state.shipping.lastName.value = shippingAddress.lastName;
        state.shipping.address.value = shippingAddress.streetName;
        state.shipping.address2.value = shippingAddress.additionalStreetInfo;
        state.shipping.zipCode.value = shippingAddress.postalCode;
        state.shipping.city.value = shippingAddress.city;
        state.shipping.state.value = shippingAddress.state;
        state.shipping.phone.value = shippingAddress.phone;
        state.shipping.country.value = payload.countryName;
      }

      let billingAddress = payload.billingAddress;
      if (billingAddress) {
        state.billing.firstName.value = billingAddress.firstName;
        state.billing.lastName.value = billingAddress.lastName;
        state.billing.address.value = billingAddress.streetName;
        state.billing.address2.value = billingAddress.additionalStreetInfo;
        state.billing.zipCode.value = billingAddress.postalCode;
        state.billing.city.value = billingAddress.city;
        state.billing.state.value = billingAddress.state;
        state.billing.country.value = payload.countryName;
      }
    });
    builder.addCase(getShippingMethods.fulfilled, (state, { payload }) => {
      state.loading = false;
      let shippingMethods = payload.shippingMethods.shippingMethods;
      if (shippingMethods?.length > 0) {
        state.shippingMethods = [...shippingMethods];
        const shippingMethodOnCart = payload.cart.shippingInfo?.id;
        state.selectedShippingMethod = shippingMethodOnCart ?? shippingMethods[0].id;
      }
    });
    builder.addCase(getExternalShippingMethods.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.nshiftOptionsData = payload;
    });
    builder.addCase(setShippingMethod.fulfilled, (state, { payload }) => {
      state.loading = false;
      const { shippingInfo } = payload;
      state.selectedShippingMethod = shippingInfo.id;
      state.shippingTotal = shippingInfo.price;
    });
    builder.addMatcher(
      isAnyOf(
        lookupShippingAddress.pending,
        setShippingMethod.pending,
        getShippingMethods.pending,
        getExternalShippingMethods.pending,
        lookupShippingAddress.pending,
        handlePfasProducts.pending,
        setShippingAddress.pending,
        setContact.pending
      ),
      (state) => {
        state.loading = true;
      }
    );
    builder.addMatcher(
      isAnyOf(
        lookupShippingAddress.rejected,
        setShippingMethod.rejected,
        getShippingMethods.rejected,
        getExternalShippingMethods.rejected,
        lookupShippingAddress.rejected,
        handlePfasProducts.rejected,
        showBillingFields.rejected,
        setShippingAddress.rejected,
        setContact.rejected,
        setContact.fulfilled
      ),
      (state) => {
        state.loading = false;
      }
    );
  },
});

export const enableSaveContact = createSelector(
  (state) => state.Shipping?.email,
  (email) => Boolean(email?.touched && email?.value && !email?.hasError)
);

export const enableSaveShippingAddress = createSelector(
  (state) => state,
  (state) => {
    if (!state.Shipping?.reintroduceContactPage && !(state.Shipping?.email.touched && state.Shipping?.email.value))
      return false;
    if (!(state.Shipping?.shipping.firstName.touched && state.Shipping?.shipping.firstName.value)) return false;
    if (!(state.Shipping?.shipping.lastName.touched && state.Shipping?.shipping.lastName.value)) return false;
    if (!(state.Shipping?.shipping.address.touched && state.Shipping?.shipping.address.value)) return false;
    if (!(state.Shipping?.shipping.city.touched && state.Shipping?.shipping.city.value)) return false;
    if (!(state.Shipping?.shipping.zipCode.touched && state.Shipping?.shipping.zipCode.value)) return false;
    if (!(state.Shipping?.shipping.phone.touched && state.Shipping?.shipping.phone.value)) return false;
    if (
      showStateField(window._THULEDATA._Checkout.states) &&
      !(state.Shipping?.shipping.state.touched && state.Shipping?.shipping.state.value)
    )
      return false;

    if (state.Shipping?.isRestrictedPostalCode) return false;

    if (
      state.Shipping?.shippingRestrictions.isRestrictedState &&
      state.Cart.lineItems.filter((x) => x.hasShippingRestriction)?.length > 0
    )
      return false;

    if (
      !state.Cart?.lineItems?.some((x) => x) ||
      state.Cart?.hasProductWithNoPrice ||
      state.Cart?.lineItems?.filter((x) => !x.isInStock)?.length > 0
    )
      return false;

    return true;
  }
);

export const getShippingRestrictions = createSelector(
  (state) => state,
  (state) => {
    let restrictions = [];
    if (state.Shipping.shippingRestrictions.isRestrictedState) {
      const productsWithShippingRestriction = state.Cart.lineItems.filter((x) => x.hasShippingRestriction);
      if (productsWithShippingRestriction?.length > 0) {
        let restrictedProducts = productsWithShippingRestriction.map((x) => x.webtitle1).join(', ');
        restrictions.push({
          message: state.Translations.shipping.shippingRestriction.replace('{0}', `${restrictedProducts}`),
        });
      } else {
        restrictions.push({ message: state.Translations.shipping.additionalFee });
      }
    }

    return restrictions;
  }
);

export const {
  setEmail,
  updateAddressField,
  validateAddressField,
  validateShippingAddress,
  validateBillingAddress,
  setUseSameAddress,
  setShippingAddressFromUser,
  setShippingAddressFromCart,
  setBillingAddressFromCart,
  setCountry,
  goToContact,
  goToShippingAddress,
  goToBillingAddress,
  goToShippingMethods,
  setBillingAddressToShippingAddress,
  setSelectedSuggestion,
  setIsValidated,
  setSelectedShippingMethod,
  setSelectedExternalShippingMethod,
  addMessage,
  removeMessage,
  clearMessages,
  goToPayment,
  setWantNewsletter,
  setAlreadySignedUpForNewsletter,
  validateEmail,
  setFormattedPhoneNumber,
  resetValidationError,
  resetEmailError,
} = ShippingSlice.actions;

export default ShippingSlice.reducer;
